import { useEffect, useState } from 'react';
import { Connection, Keypair, LAMPORTS_PER_SOL, PublicKey, TransactionInstruction, TransactionMessage, VersionedTransaction } from '@solana/web3.js';
import { useSplToken } from '../../hooks/useSplToken';
import { useRaydium } from '../../hooks/useRaydium';
import { useJito } from '../../hooks/useJito';
import { useSolana } from '../../hooks/useSolana';
import { useOpenbook } from '../../hooks/useOpenbook';
import axios from 'axios';
import pvmb from '../../utils/pvmb';
import {
    AuthorityType,
    createAssociatedTokenAccountInstruction,
    createSetAuthorityInstruction,
    createTransferInstruction,
    getAccount,
    getAssociatedTokenAddressSync,
} from '@solana/spl-token';
import { encode } from 'bs58';
import { buildSimpleTransaction, InnerSimpleV0Transaction, LOOKUP_TABLE_CACHE, MAINNET_PROGRAM_ID, TxVersion, WSOL } from '@raydium-io/raydium-sdk';
import { openbookProgramId } from '../../utils/constants';
import { formatAmmKeysByAccountData } from '../../utils/format-amm-keys-by-accountData';
import { useBackend } from '../../hooks/useBackend';
import deployAmmPoolData from '../../interfaces/deploy-amm-pool-data';
import BN from 'bn.js';
import '../../RapidLaunch.css';
import { EVENT_QUEUE_HEADER_SIZE, EVENT_SIZE, ORDERBOOK_HEADER_SIZE, ORDERBOOK_NODE_SIZE, REQUEST_QUEUE_HEADER_SIZE, REQUEST_SIZE } from '../../utils/serum';
import getRandomizedArray from '../../utils/get-randomized-array';
import { useSessionGateway } from '../../hooks/useSessionGateway';
import { Api } from '@sodamnfoolish/spl-factory-backend-types';
import { logServerLog } from '../../utils/spl-factory-backend/session';
import { useFeedGeteway } from '../../hooks/useFeedGateway';
import { TokenMetadata } from '../../utils/token-metadata';
import { useUrlParams } from '../../hooks/useUrlParams';
import { rapidLaunchJitoTip, rapidLaunchMixerJitoTip } from '../../../ConfigFees';
import {
    rapidLaunchBaseTotalLiquidity,
    rapidLaunchBundleBuyLimit,
    rapidLaunchDisperseForBundleBuyFeesExtra,
    rapidLaunchDisperseForFreezer,
    rapidLaunchDisperseForFunder,
    rapidLaunchDisperseForHolder,
    rapidLaunchDisperseForMixWalletFees,
    rapidLaunchMintAmount,
    rapidLaunchQuoteTokenInitialLiquidity,
    rapidLaunchTransactionsNumber,
    rapidLaunchTransferToHolderPercent,
    rapidLaunchVolumeBuyExtra,
} from '../../../ConfigConstants';

export const REMOTE_VOLUME_BUYER_SWITCH = 'remote-volume-buyer-switch';
export const REMOTE_REMOVE_99_9_LP_TOKEN = 'remote-remove-99-9-lp-token';
export const REMOTE_REMOVE_100_LP_TOKEN = 'remote-remove-100-lp-token';
export const REMOTE_VOLUME_SELLER_SWITCH = 'remote-volume-seller-switch';
export const REMOTE_TOKEN_DEPLOY = 'deploy-token';

export function RapidLaunch({
    connection,
    deployer,
    funder,
    holder,
    freezer,
    bundleBuyWallets,
    bundleBuyValues,
    volumeBuyWallets,
    volumeBuyAmounts,
    mixWallets,
    logMessage,
    injectToken,
    tokenAddress,
    setDeployedMarketId,
    deployedMarketId,
    mixKeypairs,
    volFunder,
    role,
}: {
    connection: Connection;
    deployer: Keypair;
    funder: Keypair;
    holder: Keypair;
    freezer: Keypair;
    bundleBuyWallets: Keypair[];
    bundleBuyValues: number[];
    volumeBuyWallets: Keypair[];
    volumeBuyAmounts: number[];
    mixWallets: Keypair[];
    logMessage: (message: string, type?: 'success' | 'error' | 'warn') => void;
    injectToken: (tokenAddress: string) => void;
    tokenAddress: string;
    setDeployedMarketId: React.Dispatch<React.SetStateAction<string | undefined>>;
    deployedMarketId: string | undefined;
    mixKeypairs: Keypair[];
    volFunder: Keypair | undefined;
    role: string;
}) {
    const { sessionId } = useUrlParams();
    const { multipleSolanaTrasfersWithJito } = useSolana();
    const {
        getCreateMintInstructions,
        getMintToInstructions,
        getUpdateMetadataInstructions,
        getCreateTokenAccountInstructions,
        getSigners,
        getWalletTokenAccounts,
        getWalletsTokenStringBalances,
    } = useSplToken();
    const { sendBundle, executeInstructions } = useJito();
    const { getCreateOpenbookMarketTransactions } = useOpenbook();
    const { getSwapInstructions, getdeployAmmPoolInstructions, buildSimpleTransactionWithInstructionsArray, fetchMarketAccountsByMints } = useRaydium();
    const { parseToken } = useBackend();
    const { sessionClient } = useSessionGateway();
    const { feedGetewayClient } = useFeedGeteway();

    const [isTokenCreated, setIsTokenCreated] = useState<boolean>(false);
    const [isMarketCreated, setIsMarketCreated] = useState<boolean>(false);
    const [isTokenDispersed, setIsTokenDispersed] = useState<boolean>(false);
    const [isPoolCreated, setIsPoolCreated] = useState<boolean>(false);
    const [tokenMintInput, setTokenMintInput] = useState<string>('');

    const [disperseAfterDeployment, setDisperseAfterDeployment] = useState<boolean>(true);

    const [remoteVolumeBuyerEnabled, setRemoteVolumeBuyerEnabled] = useState<boolean>(false);
    const [remoteVolumeSellerEnabled, setRemoteVolumeSellerEnabled] = useState<boolean>(false);

    const [remoteName, setRemoteName] = useState<string>('');
    const [remoteSymbol, setRemoteSymbol] = useState<string>('');
    const [remoteDescription, setRemoteDescription] = useState<string>('');
    const [remoteTelegram, setRemoteTelegram] = useState<string>('');
    const [remoteTwitter, setRemoteTwitter] = useState<string>('');
    const [remoteWebsite, setRemoteWebsite] = useState<string>('');
    const [remoteImage, setRemoteImage] = useState<string>('');
    const [remoteTokenDetected, setRemoteTokenDetected] = useState<boolean>(false);

    useEffect(() => {
        if (role.toLowerCase() !== 'feedlistener') return;

        if (feedGetewayClient) {
            feedGetewayClient.on(REMOTE_TOKEN_DEPLOY, (params: any[]) => {
                const [appName, roomId, metadata] = params;
                console.log('remote token detected', appName, roomId, metadata);
                console.log('roomId', roomId);
                if (roomId !== sessionId) {
                    console.log(`wrong session launch detected: ${roomId} !== ${sessionId}`);
                    return;
                }
                if (appName !== 'SPL') {
                    console.log('wrong app launch detected');
                    return;
                }

                logMessage('Remote token detected', 'success');
                setRemoteTokenDetected(true);
                setRemoteName(metadata.tokenName);
                setRemoteSymbol(metadata.tokenSymbol);
                setRemoteDescription(metadata.tokenDescription);
                setRemoteImage(metadata.customImageUrl);
                setRemoteTelegram(metadata.telegram);
                setRemoteTwitter(metadata.twitter);
                setRemoteWebsite(metadata.website);

                logMessage(`Name: ${metadata.tokenName}`);
                logMessage(`Symbol: ${metadata.tokenSymbol}`);
                logMessage(`Description: ${metadata.tokenDescription}`);
                logMessage(`Image: ${metadata.customImageUrl}`);
                logMessage(`Auto deploy: ${metadata.autoDeploy}`);
                logMessage(`Telegram: ${metadata.telegram}`);
                logMessage(`Twitter: ${metadata.twitter}`);
                logMessage(`Website: ${metadata.website}`);

                if (metadata.autoDeploy) {
                    multiLaunch(undefined, metadata);
                }
            });
        }

        return () => {
            if (feedGetewayClient) {
                feedGetewayClient.off(REMOTE_TOKEN_DEPLOY);
            }
        };
    }, [role, sessionId, feedGetewayClient]);

    useEffect(() => {
        const checkHolderBalance = async () => {
            if (!tokenAddress || !holder) {
                setIsTokenDispersed(false);
            } else {
                const holderTokenBalance = await getWalletsTokenStringBalances(connection, [holder.publicKey], new PublicKey(tokenAddress));
                holderTokenBalance[0] === '0' ? setIsTokenDispersed(true) : setIsTokenDispersed(false);
            }
        };

        const intervalId = setInterval(() => {
            checkHolderBalance();
        }, 3000);

        return () => clearInterval(intervalId);
    }, [tokenAddress, holder]);

    const tokenMintInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        let temporaryTokenMint = event.target.value;

        if (temporaryTokenMint.includes('pump.fun')) {
            const url = new URL(temporaryTokenMint);
            const path = url.pathname.substring(1);
            setTokenMintInput(path);
        } else {
            setTokenMintInput(temporaryTokenMint);
        }
    };

    async function solDisperse(connection: Connection, walletFrom: Keypair, receivers: PublicKey[], values: number[]) {
        if (receivers.length === 0) {
            logMessage('No wallets to disperse to.');
            return;
        }
        logMessage('Dispersing sol ...');

        console.log(
            values,
            values.map((value) => value / LAMPORTS_PER_SOL),
        );
        try {
            const ids = await multipleSolanaTrasfersWithJito(
                connection,
                walletFrom,
                new Array(receivers.length).fill(walletFrom),
                receivers,
                values,
                rapidLaunchMixerJitoTip,
            );

            logMessage(`All bundles sent and confirmed.`, 'success');
        } catch (e) {
            logMessage(`Error: ${e}`);
        }
    }

    async function disperseAll() {
        const allReceivers = [
            ...bundleBuyWallets.map((wallet) => wallet.publicKey),
            ...[funder.publicKey, holder.publicKey, freezer.publicKey, volFunder?.publicKey!],
            ...volumeBuyWallets.map((wallet) => wallet.publicKey),
            ...mixWallets.map((wallet) => wallet.publicKey),
        ];
        const allValues = [
            ...bundleBuyValues.map((bundleBuyValue) => bundleBuyValue * LAMPORTS_PER_SOL + rapidLaunchDisperseForBundleBuyFeesExtra),
            ...[rapidLaunchDisperseForFunder, rapidLaunchDisperseForHolder, rapidLaunchDisperseForFreezer, 0.25 * LAMPORTS_PER_SOL],
            ...volumeBuyAmounts.map((amount) => amount * LAMPORTS_PER_SOL + rapidLaunchVolumeBuyExtra),
            ...new Array(mixWallets.length).fill(rapidLaunchDisperseForMixWalletFees),
        ];

        solDisperse(connection, deployer, allReceivers, allValues);
    }

    async function parseTokenInfo(tokenAddress: string) {
        const parsedTokenMetadata = await parseToken(tokenAddress);

        let name = parsedTokenMetadata.name;
        let symbol = parsedTokenMetadata.symbol;
        let description = parsedTokenMetadata.description;
        let imageUrl = parsedTokenMetadata.image;
        let telegram = parsedTokenMetadata.telegram;
        let twitter = parsedTokenMetadata.twitter;
        let website = parsedTokenMetadata.website;

        description = `${description} \n${telegram ? `Telegram: ${telegram}` : ''} \n${twitter ? `Twitter: ${twitter}` : ''} \n${website ? `Website: ${website}` : ''}`;

        return { name, symbol, description, imageUrl };
    }

    async function getPumpTokenMetadata(tokenAddress: string) {
        const { name, symbol, description, imageUrl } = await parseTokenInfo(tokenAddress);
        if (!name || !symbol || !description || !imageUrl) {
            logMessage('Parsing failed', 'error');
            return;
        }
        return { tokenName: name, tokenSymbol: symbol, tokenDescription: description, customImageUrl: imageUrl };
    }

    async function getDeployTokenTx(tokenToCopy?: string, tokenMetadata?: TokenMetadata, decimals: number = 6) {
        const { tokenName, tokenSymbol, tokenDescription, customImageUrl } = tokenToCopy ? (await getPumpTokenMetadata(tokenToCopy))! : tokenMetadata!;

        logMessage(`Token name: ${tokenName}`);

        logMessage('Deploying token...');
        const imageUrl = customImageUrl;
        const metadataUrl = await uploadMetadataToIpfs(tokenName, tokenSymbol, tokenDescription, imageUrl);
        if (metadataUrl === undefined) {
            throw new Error('Metadata upload failed');
        }

        const instructions: TransactionInstruction[] = [];

        let customKeypair: Keypair;
        try {
            customKeypair = await pvmb.getVanity();
        } catch {
            customKeypair = Keypair.generate();
        }

        const [mintInstructions, mint] = await getCreateMintInstructions(connection, deployer, deployer.publicKey, null, decimals, customKeypair);
        instructions.push(...mintInstructions);

        const metadataInstructions = await getUpdateMetadataInstructions(deployer, mint.publicKey, tokenName, tokenSymbol, metadataUrl, false);
        instructions.push(metadataInstructions);

        const createTokenAccountInstructions = getCreateTokenAccountInstructions(deployer, mint.publicKey, deployer.publicKey);
        instructions.push(createTokenAccountInstructions);
        const tokenAccount = createTokenAccountInstructions.keys[1].pubkey;

        const mintToInstructions = await getMintToInstructions(
            mint.publicKey,
            tokenAccount,
            deployer.publicKey,
            BigInt(rapidLaunchMintAmount) * BigInt(Math.pow(10, decimals)),
        );
        instructions.push(mintToInstructions);

        const holderAssociatedToken = getAssociatedTokenAddressSync(mint.publicKey, holder.publicKey);

        const transferAmount =
            (BigInt(rapidLaunchMintAmount) * BigInt(Math.pow(10, decimals)) * BigInt(100 * rapidLaunchTransferToHolderPercent)) / BigInt(10000);
        const createInputTokenAccountInstructions = createAssociatedTokenAccountInstruction(
            deployer.publicKey,
            holderAssociatedToken,
            holder.publicKey,
            mint.publicKey,
        );
        instructions.push(createInputTokenAccountInstructions);

        const holderAccount = createInputTokenAccountInstructions.keys[1].pubkey;
        const transferToInstructions = createTransferInstruction(tokenAccount, holderAccount, deployer.publicKey, transferAmount);
        instructions.push(transferToInstructions);

        const [currentAuthorityPublicKey] = getSigners(deployer.publicKey, []);
        const setMintAuthInstructions = createSetAuthorityInstruction(mint.publicKey, currentAuthorityPublicKey, AuthorityType.MintTokens, null);
        instructions.push(setMintAuthInstructions);

        const { blockhash } = await connection.getLatestBlockhash();
        const deployMintMessage = new TransactionMessage({
            payerKey: deployer.publicKey,
            recentBlockhash: blockhash,
            instructions: instructions,
        }).compileToV0Message();

        const deployTransaction = new VersionedTransaction(deployMintMessage);
        deployTransaction.sign([deployer, mint]);

        return { tokenDeployTransaction: deployTransaction, tokenMint: mint.publicKey.toBase58() };
    }

    async function uploadMetadataToIpfs(name: string, symbol: string, description: string, image: string) {
        const tokenMetadata = JSON.stringify({
            name: name,
            symbol: symbol,
            description: description,
            image: image,
        });

        const uploadedData = await axios.post('https://api.pinata.cloud/pinning/pinJSONToIPFS', tokenMetadata, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${process.env.REACT_APP_PINATA_JWT}`,
            },
        });
        const metadataUrl = 'https://gateway.pinata.cloud/ipfs/' + uploadedData.data.IpfsHash;
        console.log('metadataUrl', metadataUrl);
        return metadataUrl;
    }

    async function deployMarket(
        mintAddress: string,
        lotSize: number = 1,
        tickSize: number = 0,
        totalEventQueueSize: number = 128,
        totalRequestQueueSize: number = 1,
        totalOrderbookSize: number = 201,
    ) {
        function calculateTotalAccountSize(individualAccountSize: number, accountHeaderSize: number, length: number) {
            const accountPadding = 12;
            const minRequiredSize = accountPadding + accountHeaderSize + length * individualAccountSize;

            const modulo = minRequiredSize % 8;

            return modulo <= 4 ? minRequiredSize + (4 - modulo) : minRequiredSize + (8 - modulo + 4);
        }

        const eventQueueLengthBytes = calculateTotalAccountSize(totalEventQueueSize, EVENT_QUEUE_HEADER_SIZE, EVENT_SIZE);
        const requestQueueLengthBytes = calculateTotalAccountSize(totalRequestQueueSize, REQUEST_QUEUE_HEADER_SIZE, REQUEST_SIZE);
        const orderbookLengthBytes = calculateTotalAccountSize(totalOrderbookSize, ORDERBOOK_HEADER_SIZE, ORDERBOOK_NODE_SIZE);
        const marketObject = await getCreateOpenbookMarketTransactions(
            connection,
            deployer,
            mintAddress,
            WSOL.mint,
            lotSize,
            tickSize,
            eventQueueLengthBytes,
            requestQueueLengthBytes,
            orderbookLengthBytes,
            new PublicKey(openbookProgramId),
        );

        //setIsMarketCreated(true);
        return marketObject;
    }

    async function getDeployAmmPoolTxs(openBookMarketId: string, baseTokenMint: string) {
        console.log('market', openBookMarketId);
        logMessage('Deploying AMM Pool...');

        const walletTokenAccounts = await getWalletTokenAccounts(connection, deployer.publicKey);

        const deployAmmPoolData: deployAmmPoolData = {
            connection,
            programId: MAINNET_PROGRAM_ID.AmmV4,
            marketInfo: {
                marketId: new PublicKey(openBookMarketId),
                programId: MAINNET_PROGRAM_ID.OPENBOOK_MARKET,
            },
            baseMintInfo: {
                mint: new PublicKey(baseTokenMint),
                decimals: 6,
            },
            quoteMintInfo: {
                mint: new PublicKey(WSOL.mint),
                decimals: WSOL.decimals,
            },
            baseAmount: rapidLaunchBaseTotalLiquidity,
            quoteAmount: rapidLaunchQuoteTokenInitialLiquidity,
            startTime: new BN(0),
            ownerInfo: {
                feePayer: deployer.publicKey,
                wallet: deployer.publicKey,
                tokenAccounts: walletTokenAccounts,
                useSOLBalance: true,
            },
            associatedOnly: false,
            computeBudgetConfig: { microLamports: 1000 },
            checkCreateATAOwner: true,
            makeTxVersion: TxVersion.V0,
            feeDestinationId: new PublicKey('7YttLkHDoNj9wyDur5pM1ejNaAvT9X4eqaYcHQqtj2G5'), //7YttLkHDoNj9wyDur5pM1ejNaAvT9X4eqaYcHQqtj2G5
        };
        console.log(
            'token accounts',
            walletTokenAccounts.map((account) => account.pubkey.toBase58()),
        );
        const deployPoolInstructions = await getdeployAmmPoolInstructions(deployAmmPoolData);

        const deploymentVersionedTransaction = (
            await buildSimpleTransaction({
                connection,
                makeTxVersion: TxVersion.V0,
                payer: deployer.publicKey,
                innerTransactions: deployPoolInstructions.innerTransactions,
                addLookupTableInfo: LOOKUP_TABLE_CACHE,
            })
        )[0] as VersionedTransaction;
        deploymentVersionedTransaction.sign([deployer]);

        const jitoVersionedTransactions: VersionedTransaction[] = [];

        jitoVersionedTransactions.push(deploymentVersionedTransaction);
        //making swap transaction
        const ammId = deployPoolInstructions.address.ammId;
        const lpMint = deployPoolInstructions.address.lpMint;

        const simulated = await connection.simulateTransaction(deploymentVersionedTransaction, {
            accounts: {
                encoding: 'base64',
                addresses: [ammId.toBase58(), lpMint.toBase58()],
            },
            commitment: 'processed',
        });
        console.log('simulation result', simulated);
        if (
            simulated.value.accounts === null ||
            simulated.value.accounts === undefined ||
            simulated.value.accounts[0] === null ||
            simulated.value.accounts[1] === null
        ) {
            logMessage('Simulated accounts are null', 'error');
            return [];
        }

        const ammAccountInfo = simulated.value.accounts[0];
        const lpMintAccountInfo = simulated.value.accounts[1];

        const ammInfoBuffer = Buffer.from(ammAccountInfo.data[0], 'base64');
        const lpMintInfoBuffer = Buffer.from(lpMintAccountInfo.data[0], 'base64');
        const poolKeys = await formatAmmKeysByAccountData(connection, ammId, ammInfoBuffer, lpMintInfoBuffer, new PublicKey(ammAccountInfo.owner));

        // mintIn = FROM WALLET TO RAYDIUM
        // mintOut = FROM RAYDIUM TO WALLET
        let mintIn, mintOut;
        if (poolKeys.baseMint.toBase58() === WSOL.mint) {
            mintIn = poolKeys.baseMint;
            mintOut = poolKeys.quoteMint;
        } else if (poolKeys.quoteMint.toBase58() === WSOL.mint) {
            mintIn = poolKeys.quoteMint;
            mintOut = poolKeys.baseMint;
        } else {
            logMessage('Only SOL is supported as purchase asset');
            return [];
        }

        if (bundleBuyWallets.length < 1) {
            logMessage('No bundleBuyWallets found in CSV', 'error');
            return [];
        }
        if (bundleBuyWallets.length !== bundleBuyValues.length) {
            logMessage('bundleBuyWallets and SOL numbers do not match', 'error');
            return [];
        }
        if (bundleBuyWallets.length > 4) {
            logMessage(`bundleBuyWallets limit excided. Max ${rapidLaunchBundleBuyLimit} bundleBuyWallets allowed in one bundle.`, 'error');
            bundleBuyWallets = bundleBuyWallets.slice(0, rapidLaunchBundleBuyLimit);
        }

        const swapInstructionsArray: InnerSimpleV0Transaction[][] = [[], [], [], []];
        const signersArray: Keypair[][] = [[], [], [], []];
        for (let i = 0; i < bundleBuyWallets.length; i++) {
            const swapInstructions = await getSwapInstructions(
                connection,
                bundleBuyWallets[i],
                bundleBuyWallets[i],
                poolKeys,
                new BN(bundleBuyValues[i] * LAMPORTS_PER_SOL),
                1,
                mintIn,
                mintOut,
                'in',
                0,
            );

            swapInstructionsArray[i % rapidLaunchTransactionsNumber].push(...swapInstructions);
            signersArray[i % rapidLaunchTransactionsNumber].push(bundleBuyWallets[i]);
        }
        let { blockhash } = await connection.getLatestBlockhash('finalized');
        for (const [transactionNumber, transactionSwapInstructions] of swapInstructionsArray.entries()) {
            // let jitoTipPayer: Keypair | undefined = undefined;
            // let jitoTipAmount: number | undefined = undefined;

            const versionedTransaction = (await buildSimpleTransactionWithInstructionsArray({
                connection,
                makeTxVersion: TxVersion.V0,
                payer: signersArray[transactionNumber][0].publicKey,
                innerTransactions: transactionSwapInstructions,
                addLookupTableInfo: LOOKUP_TABLE_CACHE,
                recentBlockhash: blockhash,
                signers: signersArray[transactionNumber],
                jitoTipPayer: signersArray[transactionNumber][0],
                jitoTipAmount: rapidLaunchJitoTip,
            })) as VersionedTransaction;
            versionedTransaction.sign([signersArray[transactionNumber][0]]);
            jitoVersionedTransactions.push(versionedTransaction);
        }
        console.log(
            'deploy pool signatures',
            jitoVersionedTransactions.map((tx) => encode(tx.signatures[0])),
        );

        return jitoVersionedTransactions;
        // const id = await sendBundle(connection, jitoVersionedTransactions, jitoFeePayer, poolJitoTip * LAMPORTS_PER_SOL, true);
        // logMessage(`JITO bundle sent: ${id}`);

        // await connection.confirmTransaction({ blockhash: blockhash, lastValidBlockHeight: lastValidBlockHeight, signature: sigToConfirm }, 'processed');
        // logMessage(`Deploy pool tx confirmed: ${sigToConfirm}`, 'success');
    }

    async function multiLaunch(pumpToken?: string, tokenMetadata?: TokenMetadata) {
        logMessage('Launching pump imitation...');
        const tokenLaunchBundle: VersionedTransaction[] = [];
        const { tokenDeployTransaction, tokenMint } = (await getDeployTokenTx(pumpToken, tokenMetadata))!;
        tokenLaunchBundle.push(tokenDeployTransaction);

        const { vaultTransaction, marketTransaction, marketId } = await deployMarket(tokenMint);
        tokenLaunchBundle.push(vaultTransaction, marketTransaction);

        const id = await sendBundle(connection, tokenLaunchBundle, deployer, rapidLaunchJitoTip);
        logMessage(`Token and Market bundle sent: ${id}`);

        let { lastValidBlockHeight, blockhash } = await connection.getLatestBlockhash('finalized');
        await connection.confirmTransaction(
            {
                blockhash: blockhash,
                lastValidBlockHeight: lastValidBlockHeight,
                signature: encode(tokenDeployTransaction.signatures[0]),
            },
            'confirmed',
        );
        await new Promise((resolve) => setTimeout(resolve, 3000));
        setIsTokenCreated(true);
        setIsMarketCreated(true);
        injectToken(tokenMint);
        logMessage('Market ID: ' + marketId);
        setDeployedMarketId(marketId);
        logMessage(`Token and Market bundle confirmed`, 'success');

        if (disperseAfterDeployment) {
            mixTokenOneWave(tokenMint);
        }
    }

    async function launchPool(marketId: string, tokenMint: string) {
        if (!marketId || !tokenMint) {
            logMessage('Market ID or token mint is missing', 'error');
            return;
        }

        logMessage('Launching pool...');
        const poolBundle = await getDeployAmmPoolTxs(marketId, tokenMint);
        const bundleId = await sendBundle(connection, poolBundle, deployer, rapidLaunchJitoTip, true);
        logMessage(`Pool bundle sent: ${bundleId}`);

        const { lastValidBlockHeight, blockhash } = await connection.getLatestBlockhash('finalized');
        await connection.confirmTransaction(
            {
                blockhash: blockhash,
                lastValidBlockHeight: lastValidBlockHeight,
                signature: encode(poolBundle[0].signatures[0]),
            },
            'processed',
        );
        setIsPoolCreated(true);
        logMessage(`Pool bundle confirmed`, 'success');
    }

    async function mixTokenOneWave(tokenAddress: string, transferPrecision = 100000) {
        try {
            logMessage('Dispersing with one wave...');

            const holderTokenAccount = getAssociatedTokenAddressSync(new PublicKey(tokenAddress), holder.publicKey);
            const holderBalance = (await getAccount(connection, holderTokenAccount, 'processed')).amount;
            if (!holderBalance) {
                logMessage('Holder balance is 0', 'error');
                return;
            }

            const randomArraySpendPromile = getRandomizedArray(transferPrecision, mixWallets.length, 20);
            const instructionChanks: TransactionInstruction[][] = [];
            const signersChanks: Keypair[][] = [];
            for (const [index, mixWallet] of mixKeypairs.entries()) {
                const instructions: TransactionInstruction[] = [];
                const signers: Keypair[] = [];

                const mixTokenAccount = getAssociatedTokenAddressSync(new PublicKey(tokenAddress), mixWallet.publicKey);
                const accountData = await connection.getAccountInfo(mixTokenAccount, 'processed');
                if (!accountData) {
                    const createTokenAccountInstructions = createAssociatedTokenAccountInstruction(
                        mixWallet.publicKey,
                        mixTokenAccount,
                        mixWallet.publicKey,
                        new PublicKey(tokenAddress),
                    );
                    instructions.push(createTokenAccountInstructions);
                    signers.push(mixWallet);
                }

                const transferAmount = (holderBalance * BigInt(randomArraySpendPromile[index])) / BigInt(transferPrecision);
                const transferToInstructions = createTransferInstruction(holderTokenAccount, mixTokenAccount, holder.publicKey, transferAmount);
                instructions.push(transferToInstructions);
                signers.push(holder);

                instructionChanks.push(instructions);
                signersChanks.push(signers);
            }

            const chunksPerTransaction = 8;
            for (let i = 0; i < instructionChanks.length; i += chunksPerTransaction) {
                const fullInstructions = instructionChanks.slice(i, i + chunksPerTransaction).flat();
                const fullSigners = signersChanks.slice(i, i + chunksPerTransaction).flat();

                executeInstructions(connection, fullInstructions, fullSigners, holder, rapidLaunchMixerJitoTip, false, logMessage);
                await new Promise((resolve) => setTimeout(resolve, 250));
            }
            logMessage('Token mixing bundles sent.');
        } catch (error) {
            logMessage(`Mixing: ${error}`, 'error');
        }
    }

    async function switchRemoteVolumeBuyer() {
        const hasError = await new Promise<boolean>((resolve) => {
            sessionClient!.emit(REMOTE_VOLUME_BUYER_SWITCH, !remoteVolumeBuyerEnabled, (serverLogs: Array<Api.Session.Gateway.ServerLog>) => {
                serverLogs.forEach((serverLog) => logServerLog(serverLog, logMessage));
                resolve(serverLogs.some((serverLog) => serverLog.level == Api.Common.Gateway.LogLevel.Error));
            });
        });

        if (hasError) return;

        setRemoteVolumeBuyerEnabled(!remoteVolumeBuyerEnabled);
    }

    async function switchRemoteVolumeSeller() {
        const hasError = await new Promise<boolean>((resolve) => {
            sessionClient!.emit(REMOTE_VOLUME_SELLER_SWITCH, !remoteVolumeSellerEnabled, (serverLogs: Array<Api.Session.Gateway.ServerLog>) => {
                serverLogs.forEach((serverLog) => logServerLog(serverLog, logMessage));
                resolve(serverLogs.some((serverLog) => serverLog.level == Api.Common.Gateway.LogLevel.Error));
            });
        });

        console.log(hasError);

        if (hasError) return;

        setRemoteVolumeSellerEnabled(!remoteVolumeSellerEnabled);
    }

    return (
        <section>
            <h2> Rapid Launch</h2>
            <section className='buttons'>
                <div className='but-token'>
                    Token&nbsp;&nbsp;
                    {isTokenCreated ? '✅' : '❌'}
                </div>
                <div className='but-market'>
                    Market&nbsp;&nbsp;
                    {isMarketCreated ? '✅' : '❌'}
                </div>
                <div className='but-dispersed'>
                    Dispersed&nbsp;&nbsp;
                    {isTokenDispersed ? '✅' : '❌'}
                </div>
                <div className='but-pool'>
                    Pool&nbsp;&nbsp;
                    {isPoolCreated ? '✅' : '❌'}
                </div>
            </section>
            <h2>Preparations</h2>
            <section className='preparations'>
                <button className='button-predisperse' onClick={() => disperseAll()}>
                    Predisperse SOL All Wallets
                </button>
            </section>
            <h2>Token to copy</h2>
            <section className='disperse-mix'>
                <div className='disperse-to-mix'>
                    Disperse to mix after deployment
                    <input type='checkbox' checked={disperseAfterDeployment} onClick={() => setDisperseAfterDeployment(!disperseAfterDeployment)}></input>
                </div>
            </section>
            <section className='token-copy'>
                {role.toLowerCase() !== 'feedlistener' ? (
                    <input type='text' value={tokenMintInput} onChange={tokenMintInputChange} />
                ) : remoteTokenDetected ? (
                    <p>Token detected</p>
                ) : (
                    <p>No token detected</p>
                )}
                <button
                    className='button-accent'
                    onClick={() => {
                        role.toLowerCase() === 'feedlistener'
                            ? multiLaunch(undefined, {
                                  tokenName: remoteName,
                                  tokenSymbol: remoteSymbol,
                                  tokenDescription: remoteDescription,
                                  tokenTelegram: remoteTelegram,
                                  tokenTwitter: remoteTwitter,
                                  tokenWebsite: remoteWebsite,
                                  customImageUrl: remoteImage,
                                  autoDeploy: false,
                              })
                            : multiLaunch(tokenMintInput, undefined);
                    }}
                    disabled={role.toLowerCase() === 'feedlistener' && !remoteTokenDetected}
                >
                    FULL START
                </button>
            </section>
            <section className='continue-mix'>
                <div className='continue-pool'>
                    <button
                        className='cont-btn'
                        onClick={() => launchPool(deployedMarketId || '', tokenAddress)}
                        disabled={!deployedMarketId || !tokenAddress || isPoolCreated}
                    >
                        CONTINUE POOL
                    </button>
                </div>
                <div className='mix-token'>
                    <button className='mix-btn' onClick={() => mixTokenOneWave(tokenAddress)} disabled={!tokenAddress || isTokenDispersed}>
                        MIX TOKEN
                    </button>
                </div>
            </section>
            {role === 'MASTER' ? (
                <section className='remote-slave-control'>
                    <h1 className='remote'>Remote Slave Control</h1>
                    <div className='switcher'>
                        <label className='switch'>
                            <input disabled={!sessionClient} type='checkbox' checked={remoteVolumeBuyerEnabled} onClick={switchRemoteVolumeBuyer} />
                            <span className='slider round'></span>
                        </label>
                        <p className='pause-start'>Pause/Start</p>
                        <h1 className='volume'>Volume Buyer</h1>
                    </div>
                    <div className='remove-btns'>
                        <button
                            className='first'
                            disabled={!sessionClient}
                            onClick={() =>
                                new Promise<void>(() => {
                                    sessionClient!.emit(REMOTE_REMOVE_99_9_LP_TOKEN, (serverLogs: Array<Api.Session.Gateway.ServerLog>) =>
                                        serverLogs.forEach((serverLog) => logServerLog(serverLog, logMessage)),
                                    );
                                })
                            }
                        >
                            Remove 99.9%
                        </button>
                        <button
                            className='second'
                            disabled={!sessionClient}
                            onClick={() =>
                                new Promise<void>(() => {
                                    sessionClient!.emit(REMOTE_REMOVE_100_LP_TOKEN, (serverLogs: Array<Api.Session.Gateway.ServerLog>) =>
                                        serverLogs.forEach((serverLog) => logServerLog(serverLog, logMessage)),
                                    );
                                })
                            }
                        >
                            Remove 100%
                        </button>
                    </div>
                    <div className='volume-buy'>
                        <label className='switch'>
                            <input type='checkbox' disabled={!sessionClient} checked={remoteVolumeSellerEnabled} onClick={switchRemoteVolumeSeller} />
                            <span className='slider round'></span>
                        </label>
                        <p className='pause-start'>Pause/Start</p>
                        <h1 className='volume'>Volume Seller</h1>
                    </div>
                </section>
            ) : (
                <p></p>
            )}
        </section>
    );
}
